import React from "react";
import { Box, IconButton } from "@mui/material";

import { AtomicLogo } from "../assets/AtomicLogo";
import type { MUIFlatTableColumn } from "../components/universal_mui_table/MUIFlatTable";
import {
  openSalesOrderCellPopover,
  type SalesOrderCellPopoverTarget,
} from "../components/sales_order_cell_popover/store";
import {
  formatCellValue,
  getFormattedCell,
  isHistoricalDate,
} from "./mui_table_utils";

/**
 * Demand-page sales-order popover, on the GENERIC pivot path.
 *
 * Makes ACTUALS date cells clickable — forecast cells have no underlying
 * orders. The cell opens the popover with the row's active-segment values and
 * the column's period; a table sibling hosts the popover (viewer/index.tsx),
 * so opening never re-renders the table.
 *
 * Decorates the pivot builder's columns after the fact (the same altitude as
 * the ACTUALS / FORECAST strip) rather than teaching the shared builder about
 * Demand, and restates the exact Nucleus display value a custom renderer
 * would otherwise drop, so the Explain action keeps working on these cells.
 */

// Room reserved INSIDE the shaded box so the logo sits to the LEFT of the
// value without overlapping it. Applied to every actuals cell, drillable or
// not, so the column stays aligned.
const LEFT_GUTTER_PX = 22;

type Hierarchy = SalesOrderCellPopoverTarget["dateHierarchy"];

type DemandViewParams = {
  dateHierarchy?: string | null;
  segments?: string[] | null;
  measure?: string | null;
  filters?: unknown[] | null;
  postFilters?: unknown[] | null;
};

const HIERARCHIES: ReadonlySet<string> = new Set([
  "day",
  "week",
  "month",
  "quarter",
  "year",
]);

type DrilldownContext = {
  viewParams: DemandViewParams;
  /** The view's raw-column → short-form map (order_placed_month → month). */
  dateHierarchyColumnFilter?: Record<string, string> | null;
  /** The active run's as-of; cells at or after it are forecast. */
  runAsOf: string | number | Date | null | undefined;
  theme?: any;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  shadeHistory?: boolean;
};

const isDateColumnKey = (key: string): boolean =>
  key !== "TOTAL" && key !== "" && !Number.isNaN(Number(key));

const numericValue = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isNaN(n) ? null : n;
};

/**
 * viewParams.dateHierarchy is the raw column ("order_placed_month"), but the
 * transform expects the short form ("month"). Fall back to the raw value for
 * views that already store the short form.
 */
export function salesOrderDateHierarchy(
  viewParams: DemandViewParams,
  dateHierarchyColumnFilter?: Record<string, string> | null,
): Hierarchy | null {
  const raw = viewParams.dateHierarchy ?? "";
  const short = dateHierarchyColumnFilter?.[raw] ?? raw;
  // A raw column name the view's map doesn't cover ("order_placed_date") is
  // not something the transform understands: no popover beats a bad request.
  return HIERARCHIES.has(short) ? (short as Hierarchy) : null;
}

/**
 * The row's value for each active segment — what the transform filters by.
 * Null when any active segment is blank on this row: the transform can only
 * match values, so dropping the key would list every value of that segment
 * (a superset of the cell) rather than the blank slice the cell aggregates.
 */
export function salesOrderSegmentsFor(
  row: Record<string, unknown>,
  viewParams: DemandViewParams,
): Record<string, string> | null {
  const segments: Record<string, string> = {};
  for (const f of viewParams.segments ?? []) {
    if (row[f] == null || row[f] === "") return null;
    segments[f] = String(row[f]);
  }
  return segments;
}

function SalesOrderDrilldownCell({
  row,
  col,
  ctx,
}: {
  row: Record<string, unknown>;
  col: MUIFlatTableColumn;
  ctx: DrilldownContext;
}) {
  const content = getFormattedCell({
    value: row[col.key],
    col: col.currencyKey ?? col.key,
    columnFormatters: ctx.columnFormatters,
    row,
    theme: ctx.theme,
    columnType: col.cellType,
    headerType: col.headerType,
    shadeHistory: ctx.shadeHistory,
    align: col.align,
    leftGutterPx: LEFT_GUTTER_PX,
  });
  const value = numericValue(row[col.key]);
  const segments = salesOrderSegmentsFor(row, ctx.viewParams);
  const dateHierarchy = salesOrderDateHierarchy(
    ctx.viewParams,
    ctx.dateHierarchyColumnFilter,
  );
  if (value == null || !(value > 0) || !segments || !dateHierarchy) {
    return content;
  }

  const brandBlue = ctx.theme?.palette?.atomic?.brandBlue ?? "#3877F7";
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    // A separate, nested action: don't also fire the Nucleus cell action.
    e.stopPropagation();
    openSalesOrderCellPopover({
      anchorEl:
        (e.currentTarget.parentElement as HTMLElement) ?? e.currentTarget,
      segments,
      periodStartMs: Number(col.key),
      dateHierarchy,
      measure: ctx.viewParams.measure ?? null,
      hasViewFilters:
        (ctx.viewParams.filters?.length ?? 0) > 0 ||
        (ctx.viewParams.postFilters?.length ?? 0) > 0,
    });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        // CSS-only reveal — no React state, so hovering repaints nothing.
        "&:hover .cellDrilldownBtn": { opacity: 1, pointerEvents: "auto" },
        "&:focus-within .cellDrilldownBtn": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      {content}
      <IconButton
        className="cellDrilldownBtn"
        aria-label="View sales orders"
        onClick={onClick}
        onKeyDown={onKeyDown}
        sx={{
          position: "absolute",
          left: 2,
          top: "50%",
          transform: "translateY(-50%)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          padding: 0,
          borderRadius: "50%",
          backgroundColor: "#fff",
          "&:hover": { backgroundColor: "#fff" },
          "&:focus-visible": {
            opacity: 1,
            pointerEvents: "auto",
            outline: `2px solid ${brandBlue}`,
            outlineOffset: 1,
          },
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 80ms linear",
        }}
      >
        <AtomicLogo width={14} height={14} />
      </IconButton>
    </Box>
  );
}

/**
 * Decorate the pivot's ACTUALS date columns with the sales-order drilldown.
 * Returns the SAME array when there is no as-of to split on or no actuals
 * column in view, so callers can rely on referential stability.
 */
export function withSalesOrderCellDrilldown(
  columns: MUIFlatTableColumn[],
  ctx: DrilldownContext,
): MUIFlatTableColumn[] {
  if (ctx.runAsOf == null) return columns;
  if (!salesOrderDateHierarchy(ctx.viewParams, ctx.dateHierarchyColumnFilter)) {
    return columns;
  }
  const isActuals = (key: string) =>
    isDateColumnKey(key) && isHistoricalDate(Number(key), ctx.runAsOf as any);
  if (!columns.some((c) => isActuals(c.key) && !c.renderCell)) return columns;

  return columns.map((col) => {
    if (!isActuals(col.key) || col.renderCell) return col;
    return {
      ...col,
      // A custom renderer skips MUIFlatTable's formatted-value fallback for
      // Nucleus; restate it so Explain keeps the exact on-screen text.
      explainDisplayValue: (row: Record<string, unknown>) => {
        const formatted = formatCellValue(
          row[col.key],
          col.cellType,
          ctx.columnFormatters,
          col.currencyKey ?? col.key,
          row,
        );
        return typeof formatted === "string" || typeof formatted === "number"
          ? String(formatted)
          : undefined;
      },
      renderCell: (row: Record<string, unknown>) => (
        <SalesOrderDrilldownCell row={row} col={col} ctx={ctx} />
      ),
    };
  });
}
